import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import {
  collection, getDocs, addDoc, query, orderBy
} from 'firebase/firestore';
import {
  Box, Container, Typography, Paper, Button, TextField, IconButton,
  Chip, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, MenuItem, Divider, LinearProgress,
  InputAdornment
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditNoteIcon from '@mui/icons-material/EditNote';
import LagbasLogo from '../components/LagbasLogo';
import { SECTIONS, TOTAL_ITEMS, calcProgress } from '../utils/onboardingTemplate';

const PHASE_LABELS = {
  before:   'Innan första dag',
  day1:     'Dag 1',
  week1_2:  'Vecka 1–2',
  followup: 'Uppföljning',
};

function ProgressRing({ value, size = 52 }) {
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (value / 100) * circ;
  const color = value === 100 ? '#2E7D32' : value >= 60 ? '#FF6B00' : value >= 30 ? '#1565C0' : '#bdbdbd';
  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e0e0e0" strokeWidth={6} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={6}
          strokeDasharray={circ} strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      </svg>
      <Typography variant="caption" fontWeight="bold" sx={{
        position: 'absolute', inset: 0, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        fontSize: '0.65rem', color,
      }}>
        {value}%
      </Typography>
    </Box>
  );
}

export default function Onboarding() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const role = userProfile?.role || '';
  const canAccess = ['vd', 'superadmin', 'ao_ansvarig', 'gruppledare'].includes(role);

  const [onboardings, setOnboardings] = useState([]);
  // employees = alla som kan vara mentor (aktiva medarbetare)
  const [employees, setEmployees] = useState([]);
  // candidates = rekryteringskandidater + pending employees (kan onboardas, ej fullvärdiga ännu)
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ employeeUid: '', employeeName: '', startDate: '', mentorUid: '', responsibleUid: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    load();
  }, []); // eslint-disable-line

  // Öppna dialogen automatiskt om vi kom hit från ett signerat anställningsavtal
  useEffect(() => {
    const prefill = location.state?.prefill;
    if (prefill?.employeeUid) {
      setForm(f => ({
        ...f,
        employeeUid: prefill.employeeUid,
        employeeName: prefill.employeeName || '',
        startDate: prefill.startDate || '',
      }));
      setDialog(true);
    }
  }, [location.state]);

  async function load() {
    setLoading(true);
    const [obSnap, empSnap, pendingSnap, recruitSnap] = await Promise.all([
      getDocs(query(collection(db, 'onboarding'), orderBy('createdAt', 'desc'))),
      getDocs(collection(db, 'users')),
      getDocs(collection(db, 'pendingEmployees')),
      getDocs(collection(db, 'recruitment')),
    ]);
    const activeEmployees = empSnap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(e => e.displayName || e.email);
    setEmployees(activeEmployees);

    // Kandidater att onboarda = pendingEmployees + rekryteringskandidater
    // (ej befintliga fullvärdiga medarbetare)
    const existingIds = new Set(activeEmployees.map(e => e.id));
    const pendingList = pendingSnap.docs
      .map(d => ({ id: d.id, ...d.data(), _source: 'pending' }))
      .filter(e => (e.displayName || e.email) && !existingIds.has(e.id));
    const recruitList = recruitSnap.docs
      .map(d => {
        const data = d.data();
        // Normalisera namn – recruitment-dokument använder firstName/lastName
        const name = [data.firstName, data.lastName].filter(Boolean).join(' ').trim() || data.displayName || data.name || '';
        return { id: d.id, ...data, _source: 'recruitment', _resolvedName: name };
      })
      .filter(e => (e._resolvedName || e.email) && !existingIds.has(e.id));
    setCandidates([...pendingList, ...recruitList]);

    setOnboardings(obSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  }

  async function handleCreate() {
    if (!form.employeeUid) return;
    setSaving(true);
    try {
      // Slå upp namn från candidates (eller använd det inmatade namnet om prefill)
      const allPeople = [...employees, ...candidates];
      const person = allPeople.find(e => e.id === form.employeeUid);
      const resolvedName = form.employeeName ||
        person?._resolvedName || person?.displayName || person?.name || person?.email || '';
      const data = {
        employeeUid: form.employeeUid,
        employeeName: resolvedName,
        startDate: form.startDate,
        mentorUid: form.mentorUid,
        responsibleUid: form.responsibleUid || currentUser.uid,
        status: 'active',
        checks: {},
        createdBy: currentUser.uid,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      const ref = await addDoc(collection(db, 'onboarding'), data);
      setDialog(false);
      setForm({ employeeUid: '', employeeName: '', startDate: '', mentorUid: '', responsibleUid: '' });
      navigate(`/onboarding/${ref.id}`);
    } finally {
      setSaving(false);
    }
  }

  const filtered = onboardings.filter(o => {
    const q = search.toLowerCase();
    return !q || (o.employeeName || '').toLowerCase().includes(q);
  });

  if (!loading && !canAccess) {
    // Medarbetare ser bara sin egen onboarding
    const myOnboarding = onboardings.find(o => o.employeeUid === currentUser.uid);
    if (myOnboarding) {
      navigate(`/onboarding/${myOnboarding.id}`);
      return null;
    }
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Typography color="text.secondary">Ingen pågående onboarding hittades.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f0f2f5' }}>
      {/* Top bar */}
      <Box sx={{ bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LagbasLogo />
          <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
          <IconButton color="inherit" size="small" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">Onboarding</Typography>
            <Typography variant="caption" sx={{ color: '#aaa' }}>
              {onboardings.length} pågående
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {['vd', 'superadmin', 'ao_ansvarig'].includes(role) && (
            <Button variant="outlined" startIcon={<EditNoteIcon />}
              onClick={() => navigate('/onboarding-mall')}
              sx={{ color: '#aaa', borderColor: 'rgba(255,255,255,0.3)', '&:hover': { borderColor: 'white', color: 'white' } }}>
              Redigera mall
            </Button>
          )}
          <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setDialog(true)}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>
            Starta onboarding
          </Button>
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ py: 3 }}>

        {/* Sammanfattning */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          {[
            { label: 'Totalt', count: onboardings.length, color: '#424242' },
            { label: 'Aktiva', count: onboardings.filter(o => o.status === 'active').length, color: '#FF6B00' },
            { label: 'Klara (100%)', count: onboardings.filter(o => calcProgress(o.checks) === 100).length, color: '#2E7D32' },
          ].map(s => (
            <Paper key={s.label} sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="h5" fontWeight="bold" sx={{ color: s.color }}>{s.count}</Typography>
              <Typography variant="body2" color="text.secondary">{s.label}</Typography>
            </Paper>
          ))}
        </Box>

        {/* Sök */}
        <TextField size="small" placeholder="Sök medarbetare…"
          value={search} onChange={e => setSearch(e.target.value)}
          sx={{ mb: 2, width: 320, bgcolor: 'white', borderRadius: 1 }}
          InputProps={{ startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
            </InputAdornment>
          )}} />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <PersonAddIcon sx={{ fontSize: 48, color: '#bdbdbd', mb: 1 }} />
            <Typography color="text.secondary">Inga onboardingprocesser ännu</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialog(true)}
              sx={{ mt: 2, bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>
              Starta din första
            </Button>
          </Paper>
        ) : (
          <Paper sx={{ overflow: 'hidden' }}>
            {/* Kolumnrubriker */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 180px 80px',
              px: 2.5, py: 1, bgcolor: '#f5f5f5', borderBottom: '1px solid', borderColor: 'divider' }}>
              {['Medarbetare', 'Startdatum', 'Mentor', 'Framsteg', ''].map(h => (
                <Typography key={h} variant="caption" fontWeight="bold"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.68rem', color: '#666' }}>
                  {h}
                </Typography>
              ))}
            </Box>

            {filtered.map((ob, idx) => {
              const progress = calcProgress(ob.checks);
              const mentor = employees.find(e => e.id === ob.mentorUid);
              const doneCount = Object.values(ob.checks || {}).filter(c => c?.done).length;
              const barColor = progress === 100 ? '#2E7D32' : progress >= 60 ? '#FF6B00' : '#1565C0';
              return (
                <Box key={ob.id}
                  onClick={() => navigate(`/onboarding/${ob.id}`)}
                  sx={{
                    display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 180px 80px',
                    px: 2.5, py: 1.5, cursor: 'pointer', alignItems: 'center',
                    borderBottom: idx < filtered.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}>
                  {/* Namn */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <ProgressRing value={progress} />
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{ob.employeeName || '–'}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                        {doneCount} / {TOTAL_ITEMS} aktiviteter klara
                      </Typography>
                    </Box>
                  </Box>

                  {/* Startdatum */}
                  <Typography variant="body2" color="text.secondary">
                    {ob.startDate || '–'}
                  </Typography>

                  {/* Mentor */}
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {mentor?.displayName || mentor?.email || '–'}
                  </Typography>

                  {/* Progress bar */}
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                        {progress}% klart
                      </Typography>
                      {progress === 100 && (
                        <CheckCircleIcon sx={{ fontSize: 14, color: '#2E7D32' }} />
                      )}
                    </Box>
                    <LinearProgress variant="determinate" value={progress}
                      sx={{ height: 6, borderRadius: 3,
                        bgcolor: '#e0e0e0',
                        '& .MuiLinearProgress-bar': { bgcolor: barColor, borderRadius: 3 } }} />
                    {/* Fas-brickor */}
                    <Box sx={{ display: 'flex', gap: 0.4, mt: 0.5, flexWrap: 'wrap' }}>
                      {Object.entries(PHASE_LABELS).map(([phaseKey, phaseLabel]) => {
                        const phaseItems = SECTIONS
                          .filter(s => s.phase === phaseKey)
                          .flatMap(s => s.items);
                        const phaseDone = phaseItems.filter(item => ob.checks?.[item.id]?.done).length;
                        const phaseTotal = phaseItems.length;
                        const phasePct = phaseTotal > 0 ? Math.round((phaseDone / phaseTotal) * 100) : 0;
                        return (
                          <Chip key={phaseKey} label={`${phaseLabel.split(' ')[0]} ${phasePct}%`}
                            size="small" sx={{
                              height: 16, fontSize: '0.58rem',
                              bgcolor: phasePct === 100 ? '#E8F5E9' : '#f5f5f5',
                              color: phasePct === 100 ? '#2E7D32' : '#757575',
                            }} />
                        );
                      })}
                    </Box>
                  </Box>

                  {/* Status */}
                  <Chip
                    label={progress === 100 ? 'Klar' : ob.status === 'active' ? 'Aktiv' : ob.status}
                    size="small"
                    sx={{
                      fontSize: '0.65rem', height: 20,
                      bgcolor: progress === 100 ? '#E8F5E9' : '#FFF3E0',
                      color: progress === 100 ? '#2E7D32' : '#E65100',
                      fontWeight: 700,
                    }} />
                </Box>
              );
            })}
          </Paper>
        )}
      </Container>

      {/* Ny onboarding-dialog */}
      <Dialog open={dialog} onClose={() => { setDialog(false); setForm({ employeeUid: '', employeeName: '', startDate: '', mentorUid: '', responsibleUid: '' }); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonAddIcon sx={{ color: '#FF6B00', fontSize: 20 }} />
          Starta onboardingprocess
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* Om vi kom från ett avtal och personen är förvald – visa namn direkt */}
            {form.employeeName && form.employeeUid && candidates.length > 0 && !candidates.find(c => c.id === form.employeeUid) && employees.find(e => e.id === form.employeeUid) ? (
              <TextField label="Medarbetare *" value={form.employeeName} disabled
                helperText="Hämtad från anställningsavtalet" />
            ) : (
              <TextField select label="Ny medarbetare att onboarda *" value={form.employeeUid}
                onChange={e => {
                  const person = [...candidates, ...employees].find(p => p.id === e.target.value);
                  const name = person?._resolvedName || person?.displayName || person?.name || person?.email || '';
                  setForm(f => ({ ...f, employeeUid: e.target.value, employeeName: name }));
                }}>
                <MenuItem value=""><em>Välj person…</em></MenuItem>
                {candidates.length > 0 && [
                  <MenuItem key="__cand_header__" disabled sx={{ fontSize: '0.72rem', color: 'text.disabled', fontWeight: 700 }}>
                    — Rekryteringskandidater / ej inloggade —
                  </MenuItem>,
                  ...candidates.map(c => (
                    <MenuItem key={c.id} value={c.id}>
                      {c._resolvedName || c.displayName || c.name || c.email}
                      {c._source === 'recruitment' && (
                        <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 1 }}>
                          (rekryt)
                        </Typography>
                      )}
                    </MenuItem>
                  )),
                ]}
              </TextField>
            )}
            <TextField label="Startdatum (dag 1)" type="date" value={form.startDate}
              onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
              InputLabelProps={{ shrink: true }} />
            <TextField select label="Mentor" value={form.mentorUid}
              onChange={e => setForm(f => ({ ...f, mentorUid: e.target.value }))}>
              <MenuItem value=""><em>Välj mentor…</em></MenuItem>
              {employees.map(e => (
                <MenuItem key={e.id} value={e.id}>{e.displayName || e.email}</MenuItem>
              ))}
            </TextField>
            <TextField select label="Ansvarig chef" value={form.responsibleUid}
              onChange={e => setForm(f => ({ ...f, responsibleUid: e.target.value }))}>
              <MenuItem value=""><em>Välj ansvarig…</em></MenuItem>
              {employees
                .filter(e => ['vd', 'superadmin', 'ao_ansvarig', 'gruppledare'].includes(e.role))
                .map(e => <MenuItem key={e.id} value={e.id}>{e.displayName || e.email}</MenuItem>)}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => { setDialog(false); setForm({ employeeUid: '', employeeName: '', startDate: '', mentorUid: '', responsibleUid: '' }); }}>Avbryt</Button>
          <Button variant="contained" onClick={handleCreate}
            disabled={saving || !form.employeeUid}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>
            {saving ? 'Skapar…' : 'Starta onboarding'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
